import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, ApiError } from '@/api/client'
import type { UserProfile } from '@/shared/types'

interface AuthContextValue {
  user: UserProfile | null
  permissions: Set<string>
  loading: boolean
  refresh: () => Promise<UserProfile | null>
  logout: () => Promise<void>
  hasPermission: (key: string | null | undefined) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const profile = await api<UserProfile>('/api/auth/profile')
      setUser(profile)
      return profile
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setUser(null)
        return null
      }
      setUser(null)
      return null
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      await refresh()
      if (!cancelled) setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [refresh])

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' })
    } catch {
      /* ignore */
    }
    setUser(null)
  }, [])

  const permissions = useMemo(() => new Set(user?.permissions || []), [user])

  const hasPermission = useCallback(
    (key: string | null | undefined) => {
      if (!key) return true
      if (!user) return false
      if (!permissions.size) return true
      return permissions.has(key)
    },
    [permissions, user],
  )

  const value = useMemo(
    () => ({ user, permissions, loading, refresh, logout, hasPermission }),
    [user, permissions, loading, refresh, logout, hasPermission],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
